import { combineEpics, ofType } from 'redux-observable'
import { concat, EMPTY, interval, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil, takeWhile } from 'rxjs/operators'

import { removeUndefinedValues } from 'utils/helpers'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualCompanyId,
  selectActualFeatureFlags,
  selectPartner,
  selectSelfCompanyId,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { MINIMAL_ALLOWED_POLLING_INTERVAL } from 'containers/Polling/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  createIncidentError,
  createIncidentSuccess,
  createPresetSuccess,
  fetchFiltersError,
  fetchFiltersSuccess,
  fetchLogsEntireCountError,
  fetchLogsEntireCountSuccess,
  fetchMitigationDataError,
  fetchMitigationDataSuccess,
  fetchPresetsSuccess,
  fetchThreatLogDataError,
  fetchThreatLogDataSuccess,
  preparePayload,
  releaseFromIncidentSpamError,
  releaseFromIncidentSpamSuccess,
  releaseFromInvestigationError,
  releaseFromInvestigationSuccess,
  releaseFromSpamError,
  releaseFromSpamSuccess,
  removePresetSuccess,
  setAllFilters,
  setFilter,
  setLoader,
  updatePresetSuccess,
} from './actions'
import {
  allowListErrorMessage,
  CREATE_INCIDENT,
  CREATE_PRESET_ASYNC,
  createIncidentErrorCodes,
  FETCH_DIRECTORY_TAGS,
  FETCH_FILTERS,
  FETCH_LOGS_ENTIRE_COUNT,
  FETCH_MITIGATION_DATA_ASYNC,
  FETCH_PRESETS_ASYNC,
  FETCH_THREAT_LOG_DATA,
  PAGINATE_CHANGE,
  RELEASE_FROM_INCIDENT_SPAM_ASYNC,
  RELEASE_FROM_INVESTIGATION_ASYNC,
  RELEASE_FROM_MS_QUARANTINE_ASYNC,
  RELEASE_FROM_SPAM_ASYNC,
  REMOVE_PRESET_ASYNC,
  RESET_ALL_FILTERS,
  SET_ALL_FILTERS,
  SET_ARRIVAL_DATE_FILTER,
  SET_FILTER,
  SET_LOG_DATE_FILTER,
  THREAT_LOG_BACKGROUND_REFRESH,
  UPDATE_PRESET_ASYNC,
} from './constants'
import {
  selectAllFilters,
  selectDirectoryTagsPage,
  selectDirectoryTagsSearch,
  selectFilteredThreatLogFilters,
  selectFiltersLoader,
  selectIsPresetsLoading,
  selectReleaseFromSpamData,
  selectSavedFilters,
  selectSavedSelectedFilter,
  selectThreatLogFilters,
} from './selectors'
import {
  determineSelectedFilter,
  isFiltersEqual,
  prepareCompanyIds,
  prepareFiltersToBe,
} from './utils'

export const shouldPreventApiCall = (state) => {
  const filters = selectThreatLogFilters(state)
  const ff = selectActualFeatureFlags(state)

  return filters.log_type?.includes('Presumed Safe')
    ? ff.REMOVE_PRESUMED_SAFE_LIMIT
      ? false
      : !(filters.subject || filters.sender)
    : false
}

const fetchFiltersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_FILTERS.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.investigation.threatLog)
      return true
    }),
    mergeMap(() => {
      const state = state$.value
      return api.resolutionCenter
        .fetchThreatLogFilters(
          state.partnerOptions.selectedCompany
            ? state.partnerOptions.selectedCompany.value
            : state.app.data.id
        )
        .pipe(
          map((response) => fetchFiltersSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchFiltersError, error)))
        )
    })
  )

// Shared trigger set + gate for fetchThreatLogDataEpic / disableThreatLogLoaderEpic
// below. Single source of truth so the two epics cannot drift out of sync
// with each other's drop conditions.
const THREAT_LOG_REFRESH_TRIGGERS = [
  SET_ALL_FILTERS,
  SET_LOG_DATE_FILTER,
  SET_ARRIVAL_DATE_FILTER,
  SET_FILTER,
  RESET_ALL_FILTERS,
  PAGINATE_CHANGE,
  RELEASE_FROM_MS_QUARANTINE_ASYNC.SUCCESS,
  RELEASE_FROM_INVESTIGATION_ASYNC.SUCCESS,
  RELEASE_FROM_INVESTIGATION_ASYNC.ERROR,
  RELEASE_FROM_INCIDENT_SPAM_ASYNC.SUCCESS,
  RELEASE_FROM_INCIDENT_SPAM_ASYNC.ERROR,
  FETCH_FILTERS.SUCCESS,
  FETCH_PRESETS_ASYNC.SUCCESS,
  THREAT_LOG_BACKGROUND_REFRESH,
]

// `skip`: today's drop conditions (wrong URL, or a presets-load race).
// `disableLoader`: the old predicate's `shouldDisableLoader` branch.
const evaluateThreatLogRefreshGate = (state, action) => {
  if (!matchURL(URL_CONST.investigation.threatLog)) return { skip: true, disableLoader: false }
  const isPresetsLoading = selectIsPresetsLoading(state)
  if (action.type === FETCH_FILTERS.SUCCESS && isPresetsLoading)
    return { skip: true, disableLoader: false }
  const shouldPreventCall = shouldPreventApiCall(state) || action.payload?.preventApiCall
  const isLoading = selectFiltersLoader(state)
  const shouldDisableLoader = isLoading ? false : shouldPreventCall
  return { skip: false, disableLoader: shouldDisableLoader, isLoading, shouldPreventCall }
}

// Split into two epics rather than one filter predicate that both gates the
// fetch AND dispatches `setLoader(false)` as a side effect: folding the
// dispatch into `switchMap`'s predicate would change cancellation semantics
// — today a dropped action does NOT cancel an in-flight fetch, whereas
// `switchMap` cancels on every matching action — so the split keeps this
// epic a PURE filter feeding the existing `switchMap`, and moves the
// dispatch to disableThreatLogLoaderEpic below.
//
// The two epics are order-independent: this one emits ONLY the fetch chain
// and never reads the loader flag; disableThreatLogLoaderEpic emits ONLY
// `setLoader(false)` and reads nothing this epic writes. Their relative
// delivery order is therefore unobservable, which is what makes running them
// in either order safe. If a future edit makes either epic read what the
// other writes, this argument lapses and the split must be re-examined.
const fetchThreatLogDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(...THREAT_LOG_REFRESH_TRIGGERS),
    filter((action) => {
      const { skip, disableLoader, isLoading, shouldPreventCall } = evaluateThreatLogRefreshGate(
        state$.value,
        action
      )
      if (skip || disableLoader) return false
      return !isLoading && !shouldPreventCall
    }),
    switchMap(() => {
      const state = state$.value
      const filteredFilters = selectFilteredThreatLogFilters(state)
      return api.resolutionCenter.fetchThreatLogData(filteredFilters).pipe(
        map((response) => fetchThreatLogDataSuccess(response.response)),
        catchError((error) => of(epicsErrorHandler(fetchThreatLogDataError, error, [403]))),
        takeUntil(action$.pipe(ofType(FETCH_THREAT_LOG_DATA.CANCEL)))
      )
    })
  )

const disableThreatLogLoaderEpic = (action$, state$) =>
  action$.pipe(
    ofType(...THREAT_LOG_REFRESH_TRIGGERS),
    filter((action) => {
      const { skip, disableLoader } = evaluateThreatLogRefreshGate(state$.value, action)
      return !skip && disableLoader
    }),
    map(() => setLoader(false))
  )

const createIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_INCIDENT.INITIATE),
    filter(() => matchURL(URL_CONST.investigation.threatLog)),
    switchMap((action) => {
      const body = {
        gmail_message_id: action.payload.gmail_message_id,
        office365_message_id: action.payload.office365_message_id,
        message_id: action.payload.message_id,
        sender_address: action.payload.sender_address,
        recipient_address: action.payload.recipient_address,
        subject: action.payload.subject,
        from: 'Investigation page',
        ignore_allow_list: action.payload.ignoreAllowList,
      }
      return api.threatLog
        .createIncident(
          action.payload.incidentCreator,
          action.payload.eid,
          removeUndefinedValues(body)
        )
        .pipe(
          switchMap((response) =>
            of(
              createIncidentSuccess(action.payload.eid, response.response.incidentId),
              addToast({
                id: 'Create_Incident_Toast_Message_Success',
                button: {
                  text: 'Go to incident',
                  id: 'Create_Incident_Toast_Message_Go_To_Incident_Button',
                  onClick: () => {
                    window.location = `${URL_CONST.incidents.attackdetails}/${response?.response.incidentId}`
                  },
                },
                message: (
                  <span id="Create_Incident_Toast_Message">
                    Incident
                    <a
                      className="white cp b"
                      href={`${URL_CONST.incidents.attackdetails}/${response?.response.incidentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {` IS-${response?.response.incidentId} `}
                    </a>
                    has been successfully created
                  </span>
                ),
              })
            )
          ),
          catchError((error) => {
            const errorActions = [
              epicsErrorHandler(
                () => createIncidentError(error, action.payload),
                error,
                createIncidentErrorCodes
              ),
            ]

            const FORBIDDEN_STATUS_CODE = 403

            // Add toast only if its not allow list case
            if (
              !(
                error.status === FORBIDDEN_STATUS_CODE &&
                error.response?.message === allowListErrorMessage
              )
            ) {
              errorActions.push(
                addToast({
                  message: error.response?.message,
                  type: 'error',
                  id: 'Create_Incident_Toast_Message_Error',
                })
              )
            }

            return of(...errorActions)
          }),
          takeUntil(action$.pipe(ofType(CREATE_INCIDENT.CANCEL)))
        )
    })
  )

const fetchLogsEntireCount = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_LOGS_ENTIRE_COUNT.INITIATE),
    mergeMap(() =>
      api.resolutionCenter
        .fetchLogsEntireCount(
          state$.value.partnerOptions.selectedCompany?.value,
          selectFilteredThreatLogFilters(state$.value)
        )
        .pipe(
          map((response) => fetchLogsEntireCountSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchLogsEntireCountError, error))),
          takeUntil(action$.pipe(ofType(FETCH_LOGS_ENTIRE_COUNT.CANCEL)))
        )
    )
  )

const fetchMitigationDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_MITIGATION_DATA_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .getMitigationData(
          action.payload.company_id,
          action.payload.gmail_message_id,
          action.payload.office365_message_id,
          action.payload.message_id,
          action.payload.recipient_address,
          action.payload.sender_address,
          action.payload.subject,
          action.payload.eid || action.payload.new_eid
        )
        .pipe(
          map((response) => fetchMitigationDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchMitigationDataError, error))),
          takeUntil(action$.pipe(ofType(FETCH_MITIGATION_DATA_ASYNC.CANCEL)))
        )
    )
  )

const createPresetEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_PRESET_ASYNC.INITIATE),
    mergeMap((action) => {
      const { presetId, presetName, isChosenDefault, filters } = action.payload
      const state = state$.value
      const partner = selectPartner(state)
      const companyFilter = selectCompanyFilter(state)
      const selfCompanyId = selectSelfCompanyId(state)
      const company_ids = prepareCompanyIds(partner, companyFilter, selfCompanyId)

      const body = {
        presetId,
        presetName,
        isChosenDefault,
        company_ids,
        ...prepareFiltersToBe(filters),
      }

      return api.resolutionCenter.createPreset(body).pipe(
        mergeMap((response) =>
          of(
            createPresetSuccess(response),
            addToast({
              id: 'Create_Preset_Toast_Message_Success',
              message: <span id="Create_Preset_Toast_Message">Filter successfully created</span>,
            })
          )
        ),
        catchError((error) =>
          of(
            addToast({
              type: 'error',
              id: 'Create_Preset_Toast_Message_Error',
              message: <span id="Create_Preset_Toast_Message">Filter was not saved</span>,
            }),
            epicsErrorHandler(fetchMitigationDataError, error)
          )
        ),
        takeUntil(action$.pipe(ofType(CREATE_PRESET_ASYNC.CANCEL)))
      )
    })
  )

const updatePresetEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_PRESET_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const currentState = selectSavedFilters(state)[action.payload.presetId]
      const partner = selectPartner(state)
      const companyFilter = selectCompanyFilter(state)
      const selfCompanyId = selectSelfCompanyId(state)
      const company_ids = prepareCompanyIds(partner, companyFilter, selfCompanyId)
      const filterWasUpdated = !action.payload.isChosenDefault && !action.payload.presetName

      const {
        presetId,
        presetName = currentState.presetName,
        isChosenDefault = currentState.isChosenDefault,
        filters,
      } = action.payload

      const body = {
        presetId,
        presetName,
        isChosenDefault,
        company_ids,
        ...prepareFiltersToBe(
          !filterWasUpdated ? { ...currentState.filters, ...filters } : filters
        ),
      }

      return api.resolutionCenter.updatePreset(body).pipe(
        mergeMap((response) =>
          of(
            updatePresetSuccess(response),
            addToast({
              id: 'Update_Preset_Toast_Message_Success',
              message: <span id="Update_Preset_Toast_Message">Filter successfully updated</span>,
            })
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(fetchMitigationDataError, error),
            addToast({
              type: 'error',
              id: 'Update_Preset_Toast_Message_Error',
              message: <span id="Update_Preset_Toast_Message">Filter was not updated</span>,
            })
          )
        ),
        takeUntil(action$.pipe(ofType(UPDATE_PRESET_ASYNC.CANCEL)))
      )
    })
  )

const fetchPresetsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_PRESETS_ASYNC.INITIATE,
      CREATE_PRESET_ASYNC.SUCCESS,
      UPDATE_PRESET_ASYNC.SUCCESS,
      REMOVE_PRESET_ASYNC.SUCCESS
    ),
    switchMap(() =>
      api.resolutionCenter.fetchPresets().pipe(
        mergeMap((response) => {
          const state = state$.value
          const payload = preparePayload(response)
          const currentFilters = selectAllFilters(state)
          const selectedFilter = determineSelectedFilter(
            state?.investigation?.self,
            payload.savedFilters,
            payload.defaultFilter,
            payload.filterFromUrl
          )
          const newFilter = selectedFilter && payload.savedFilters[selectedFilter].filters
          const shouldBeUpdated = newFilter && !isFiltersEqual(currentFilters, newFilter)
          if (shouldBeUpdated && selectedFilter) {
            return of(setAllFilters(newFilter), fetchPresetsSuccess(payload))
          }
          return of(fetchPresetsSuccess(payload))
        }),
        catchError((error) => of(epicsErrorHandler(fetchMitigationDataError, error))),
        takeUntil(action$.pipe(ofType(FETCH_PRESETS_ASYNC.CANCEL)))
      )
    )
  )

const removePresetEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REMOVE_PRESET_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.removePreset({ presetId: action.payload.presetId }).pipe(
        mergeMap((response) =>
          of(
            addToast({
              id: 'Remove_Preset_Toast_Message_Success',
              message: <span id="Remove_Preset_Toast_Message">Filter successfully removed</span>,
            }),
            removePresetSuccess(response)
          )
        ),
        catchError((error) =>
          of(
            addToast({
              type: 'error',
              id: 'Remove_Preset_Toast_Message_Error',
              message: <span id="Remove_Preset_Toast_Message">Filter was not removed</span>,
            }),
            epicsErrorHandler(fetchMitigationDataError, error)
          )
        ),
        takeUntil(action$.pipe(ofType(REMOVE_PRESET_ASYNC.CANCEL)))
      )
    )
  )

const releaseMSQuarantineEmailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_FROM_MS_QUARANTINE_ASYNC.INITIATE),
    mergeMap((action) =>
      concat(
        of(
          addToast({
            type: 'info',
            id: 'Microsoft_email_released_Info',
            message: <span id="Microsoft_email_released">Request is in progress</span>,
          })
        ),
        api.settings
          .releaseMSQuarantineEmail(
            action.payload.messageId,
            action.payload.newId,
            action.payload.recipientAddress,
            action.payload.index
          )
          .pipe(
            switchMap(({ response }) => {
              const { task_id: taskId } = response
              const pollingStream$ = interval(MINIMAL_ALLOWED_POLLING_INTERVAL).pipe(
                switchMap(() => api.settings.releaseMSQuarantineEmailStatus(taskId)),
                mergeMap((statusResponse) => {
                  if (statusResponse.task_status === 'finished') {
                    return of(
                      addToast({
                        id: 'Microsoft_email_released_Success',
                        message: (
                          <span id="Microsoft_email_released">
                            The email released from Microsoft quarantine
                          </span>
                        ),
                      }),
                      RELEASE_FROM_MS_QUARANTINE_ASYNC.onSuccess(action.payload.messageId)
                    )
                  } else if (statusResponse.task_status === 'reintegration_required') {
                    return of(
                      addToast({
                        type: 'error',
                        id: 'Microsoft_email_released_Error',
                        message: (
                          <span id="Microsoft_email_released">
                            Please reintegrate Advanced Integration
                          </span>
                        ),
                      }),
                      RELEASE_FROM_MS_QUARANTINE_ASYNC.onError(action.payload.messageId)
                    )
                  } else if (
                    statusResponse.task_status === 'failed' ||
                    statusResponse.statusCode >= 400
                  ) {
                    return of(
                      addToast({
                        type: 'error',
                        id: 'Microsoft_email_released_Error',
                        message: (
                          <span id="Microsoft_email_released">
                            Something went wrong. Please try again
                          </span>
                        ),
                      }),
                      RELEASE_FROM_MS_QUARANTINE_ASYNC.onError(action.payload.messageId)
                    )
                  }
                  return EMPTY
                }),
                takeWhile(
                  (z) =>
                    z.type !== RELEASE_FROM_MS_QUARANTINE_ASYNC.SUCCESS &&
                    z.type !== RELEASE_FROM_MS_QUARANTINE_ASYNC.ERROR
                ),
                catchError((error) =>
                  of(
                    addToast({
                      type: 'error',
                      id: 'Microsoft_email_released_Error',
                      message: (
                        <span id="Microsoft_email_released">
                          Something went wrong. Please try again
                        </span>
                      ),
                    }),
                    RELEASE_FROM_MS_QUARANTINE_ASYNC.onError(action.payload.messageId),
                    console.log(error)
                  )
                )
              )
              return pollingStream$
            })
          )
      )
    )
  )

const releaseFromInvestigationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_FROM_INVESTIGATION_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyId = selectActualCompanyId(state$.value)
      return api.resolutionCenter
        .releaseFromInvestigation(
          {
            id: action.payload.id,
            index: action.payload.index,
          },
          companyId
        )
        .pipe(
          mergeMap(() =>
            of(
              releaseFromInvestigationSuccess(),
              addToast({
                id: 'Release_Email_Toast_Message_Success',
                message: (
                  <span id="Release_Email_Toast_Message_Success">
                    Email has been successfully released.
                  </span>
                ),
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(releaseFromInvestigationError, error),
              addToast({
                type: 'error',
                id: 'Release_Email_Toast_Message_Error',
                message: (
                  <div style={{ display: 'flex', flexDirection: 'column', width: '285px' }}>
                    <span style={{ fontWeight: 600 }}>Unable to release email.</span>
                    <span>We encountered an issue while attempting to release this email.</span>
                  </div>
                ),
              })
            )
          ),
          takeUntil(action$.pipe(ofType(RELEASE_FROM_INVESTIGATION_ASYNC.CANCEL)))
        )
    })
  )

const releaseFromIncidentSpamEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_FROM_INCIDENT_SPAM_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyId = selectActualCompanyId(state$.value)
      return api.resolutionCenter.releaseFromIncidentSpam({ ...action.payload }, companyId).pipe(
        mergeMap(() =>
          of(
            releaseFromIncidentSpamSuccess(),
            addToast({
              id: 'Release_Incident_Spam_Toast_Message_Success',
              message: (
                <span id="Release_Incident_Spam_Toast_Message_Success">
                  Incident spam email has been successfully released.
                </span>
              ),
            })
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(releaseFromIncidentSpamError, error),
            addToast({
              type: 'error',
              id: 'Release_Incident_Spam_Toast_Message_Error',
              message: (
                <div style={{ display: 'flex', flexDirection: 'column', width: '285px' }}>
                  <span style={{ fontWeight: 600 }}>Unable to release incident spam email.</span>
                  <span>We encountered an issue while attempting to release this email.</span>
                </div>
              ),
            })
          )
        ),
        takeUntil(action$.pipe(ofType(RELEASE_FROM_INCIDENT_SPAM_ASYNC.CANCEL)))
      )
    })
  )

const releaseFromSpamEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_FROM_SPAM_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value
      const companyId = selectActualCompanyId(state)
      const data = selectReleaseFromSpamData(state)
      return api.threatLog.releaseFromSpam(data, companyId).pipe(
        mergeMap((response) =>
          of(
            addToast({
              id: 'Release_Spam_Toast_Message_Success',
              message: (
                <span id="Release_Spam_Toast_Message">Bulk release from Spam is in progress</span>
              ),
            }),
            releaseFromSpamSuccess(response)
          )
        ),
        catchError((error) =>
          of(
            addToast({
              type: 'error',
              id: 'Release_Spam_Toast_Message_Error',
              message: <span id="Release_Spam_Toast_Message">Bulk release from Spam failed</span>,
            }),
            epicsErrorHandler(releaseFromSpamError, error)
          )
        ),
        takeUntil(action$.pipe(ofType(RELEASE_FROM_SPAM_ASYNC.CANCEL)))
      )
    })
  )

const fetchDirectoryTagsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_DIRECTORY_TAGS.INITIATE, // SET_TAGS_PAGE,
      // SET_TAGS_SEARCH
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.investigation.threatLog)
      return true
    }),
    mergeMap((action) => {
      const state = state$.value
      const companyFilter = selectCompanyFilter(state)
      // The reducer resets paging on company change, but it has not run yet.
      const companyChanged = action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      const tagsPage = companyChanged ? 1 : selectDirectoryTagsPage(state)
      const tagsSearch = companyChanged ? '' : selectDirectoryTagsSearch(state)
      return api.mailboxes
        .getTags({
          companyId: companyFilter?.value,
          onlyAutoTags: true,
          page: tagsPage,
          search: tagsSearch,
          searchByTagIds: Array.isArray(action.payload) ? action.payload : undefined,
          allTags: false,
        })
        .pipe(
          mergeMap((response) => {
            const actions = [FETCH_DIRECTORY_TAGS.onSuccess(response)]

            // Check if we had missing tag IDs and got empty response
            // Meaning we have unavailable tags, and we should remove them from the filter
            if (
              action.payload &&
              Array.isArray(action.payload) &&
              action.payload.length > 0 &&
              (!response.tags || response.tags.length === 0)
            ) {
              const selectedFilter = selectSavedSelectedFilter(state)

              actions.push(
                setFilter('tagIds', []),
                addToast({
                  type: 'warning',
                  id: 'Directory_Tags_Missing_Warning',
                  message: (
                    <span id="Directory_Tags_Missing_Message">
                      The &quot;{selectedFilter?.name || 'Selected'}&quot; filter includes
                      company-specific parameters and has been reset. Please choose a new filter or
                      switch to the correct company.
                    </span>
                  ),
                })
              )
            }

            return of(...actions)
          }),
          catchError((error) => of(epicsErrorHandler(FETCH_DIRECTORY_TAGS.onError, error))),
          takeUntil(action$.pipe(ofType(FETCH_DIRECTORY_TAGS.CANCEL)))
        )
    })
  )

export default combineEpics(
  fetchFiltersEpic,
  fetchThreatLogDataEpic,
  disableThreatLogLoaderEpic,
  createIncidentEpic,
  fetchLogsEntireCount,
  fetchMitigationDataEpic,
  createPresetEpic,
  updatePresetEpic,
  fetchPresetsEpic,
  removePresetEpic,
  releaseMSQuarantineEmailEpic,
  releaseFromSpamEpic,
  releaseFromInvestigationEpic,
  releaseFromIncidentSpamEpic,
  fetchDirectoryTagsEpic
)
